import { openDB, IDBPDatabase } from 'idb';
import type { Product, PendingAction, Offer, Bid, ChatMessage, ChatSession } from '../types';

// Keep legacy browser demo data separate; never publish it as authenticated server data.
const DB_NAME = 'agro_server_cache_v1';
const DB_VERSION = 4; // Incremented for chat

export const initDB = async (): Promise<IDBPDatabase> => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        const productStore = db.createObjectStore('products', { keyPath: 'id' });
        productStore.createIndex('category', 'category');
        productStore.createIndex('updatedAt', 'updatedAt');
        db.createObjectStore('pendingActions', { keyPath: 'id' });
      }
      if (oldVersion < 2) {
        if (!db.objectStoreNames.contains('offers')) {
          const offerStore = db.createObjectStore('offers', { keyPath: 'id' });
          offerStore.createIndex('productId', 'productId');
          offerStore.createIndex('status', 'status');
        }
      }
      if (oldVersion < 3) {
        if (!db.objectStoreNames.contains('bids')) {
          const bidStore = db.createObjectStore('bids', { keyPath: 'id' });
          bidStore.createIndex('productId', 'productId');
          bidStore.createIndex('timestamp', 'timestamp');
        }
      }
      if (oldVersion < 4) {
        if (!db.objectStoreNames.contains('messages')) {
          const msgStore = db.createObjectStore('messages', { keyPath: 'id' });
          msgStore.createIndex('chatId', 'chatId');
          msgStore.createIndex('timestamp', 'timestamp');
        }
        if (!db.objectStoreNames.contains('chatSessions')) {
          const sessionStore = db.createObjectStore('chatSessions', { keyPath: 'id' });
          sessionStore.createIndex('lastTimestamp', 'lastTimestamp');
        }
      }
    },
  });
};

export const dbService = {
  async saveProducts(products: Product[]) {
    const db = await initDB();
    const tx = db.transaction('products', 'readwrite');
    for (const product of products) {
      await tx.store.put(product);
    }
    await tx.done;
  },

  async getProducts(): Promise<Product[]> {
    const db = await initDB();
    return db.getAllFromIndex('products', 'updatedAt');
  },

  async clearProducts() {
    const db = await initDB();
    await db.clear('products');
  },

  async queueAction(action: PendingAction) {
    const db = await initDB();
    await db.put('pendingActions', action);
  },

  async getPendingActions(): Promise<PendingAction[]> {
    const db = await initDB();
    const actions = await db.getAll('pendingActions');
    return actions.sort((a, b) => a.timestamp - b.timestamp);
  },

  async removePendingAction(id: string) {
    const db = await initDB();
    await db.delete('pendingActions', id);
  },

  async updatePendingAction(action: PendingAction) {
    const db = await initDB();
    await db.put('pendingActions', action);
  },

  // --- Offer Methods ---
  async saveOffers(offers: Offer[]) {
    const db = await initDB();
    const tx = db.transaction('offers', 'readwrite');
    for (const offer of offers) {
      await tx.store.put(offer);
    }
    await tx.done;
  },

  async getOffersByProduct(productId: string): Promise<Offer[]> {
    const db = await initDB();
    return db.getAllFromIndex('offers', 'productId', productId);
  },

  // --- Bid Methods ---
  async saveBids(bids: Bid[]) {
    const db = await initDB();
    const tx = db.transaction('bids', 'readwrite');
    for (const bid of bids) {
      await tx.store.put(bid);
    }
    await tx.done;
  },

  async getBidsByProduct(productId: string): Promise<Bid[]> {
    const db = await initDB();
    return db.getAllFromIndex('bids', 'productId', productId);
  },

  // --- Chat Methods ---
  async saveMessages(messages: ChatMessage[]) {
    const db = await initDB();
    const tx = db.transaction('messages', 'readwrite');
    for (const msg of messages) {
      await tx.store.put(msg);
    }
    await tx.done;
  },

  async getMessagesByChat(chatId: string): Promise<ChatMessage[]> {
    const db = await initDB();
    return db.getAllFromIndex('messages', 'chatId', chatId);
  },

  async saveChatSessions(sessions: ChatSession[]) {
    const db = await initDB();
    const tx = db.transaction('chatSessions', 'readwrite');
    for (const session of sessions) {
      await tx.store.put(session);
    }
    await tx.done;
  },

  async getChatSessions(): Promise<ChatSession[]> {
    const db = await initDB();
    return db.getAllFromIndex('chatSessions', 'lastTimestamp');
  },

  async getChatSession(id: string): Promise<ChatSession | undefined> {
    const db = await initDB();
    return db.get('chatSessions', id);
  },

  async deleteOffer(id: string) {
    const db = await initDB();
    await db.delete('offers', id);
  }
};